// packages/api/src/uploadTransport.ts
//
// ROADMAP #131 — AN UPLOAD THAT STOPS MOVING MUST STILL END.
//
// ⚠⚠ WHY. File uploads go over REST (multipart), not through the GraphQL link #122 put a limit on, and they used a bare
// `fetch` with no signal: a phone losing signal mid-upload left the request — and a drawer that refuses to close while
// it uploads (`RentalMediaDrawer`) — waiting for good.
//
// ⚠ A TOTAL limit is wrong for an upload: a 200 MB video on a slow connection rightly takes minutes. So the limit is on
// PROGRESS: while the bytes go up, the upload is abandoned only after `stallMs` with no progress at all. Once the last
// byte is sent the server still has work to do (storing, resizing) and sends no progress, so from then on it gets
// `responseMs` to answer. Either way it ends as a `RequestTimeoutError` (a write: it may still have arrived).
//
// XMLHttpRequest, not `fetch`: only XHR reports upload progress. The factory is injectable so the guard can RUN it.

import { isAuthErrorCode } from "./authErrorHandler";
import { RequestTimeoutError } from "./requestTimeout";
import { hasSessionRefresher, refreshSessionOnce, sessionNeedsRenewal } from "./sessionRefresh";

/** No progress for this long while sending: the upload has stalled (a phone that lost signal). */
export const UPLOAD_STALL_MS = 30_000;

/** Everything sent: how long storing / resizing on the server may take before we stop waiting. */
export const UPLOAD_RESPONSE_MS = 120_000;

export interface StallLimitedUploadOptions {
  url: string;
  body: FormData;
  headers?: Record<string, string>;
  /** Send cookies (the app's session) — `credentials: "include"`. */
  withCredentials?: boolean;
  /** Abandon the upload after this long with NO progress while sending. */
  stallMs: number;
  /** Once everything is sent, how long the server may take to answer. */
  responseMs: number;
  /** The XHR to use (defaults to the browser's). */
  xhrFactory?: () => XMLHttpRequest;
}

export interface UploadAnswer {
  status: number;
  text: string;
}

/**
 * POST a multipart body, abandoning it when it stops making progress (or the server never answers once it is sent).
 * Resolves with the answer's status and text whatever the status; rejects with a `RequestTimeoutError` when abandoned,
 * or a `TypeError` when the network fails outright.
 */
export const postFormWithStallLimit = ({
  url,
  body,
  headers = {},
  withCredentials = false,
  stallMs,
  responseMs,
  xhrFactory,
}: StallLimitedUploadOptions): Promise<UploadAnswer> =>
  new Promise<UploadAnswer>((resolve, reject) => {
    const xhr = xhrFactory ? xhrFactory() : new XMLHttpRequest();
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const settle = (fn: () => void) => {
      if (settled) {
        return;
      }

      settled = true;

      if (timer) {
        clearTimeout(timer);
      }

      fn();
    };

    // (Re)start the clock: `ms` from now with nothing new, the upload is abandoned.
    const arm = (ms: number) => {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        const error = new RequestTimeoutError(ms, true);
        settle(() => {
          reject(error);
          xhr.abort();
        });
      }, ms);
    };

    xhr.upload.onprogress = () => arm(stallMs);
    // Everything is sent: the server's turn, with no progress to watch.
    xhr.upload.onload = () => arm(responseMs);
    // The answer itself streaming back is progress too.
    xhr.onprogress = () => arm(responseMs);
    xhr.onload = () => settle(() => resolve({ status: xhr.status, text: String(xhr.responseText ?? "") }));
    xhr.onerror = () => settle(() => reject(new TypeError("Network error")));
    xhr.onabort = () => settle(() => reject(new TypeError("Upload aborted")));

    xhr.open("POST", url, true);
    xhr.withCredentials = withCredentials;
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    arm(stallMs);
    xhr.send(body);
  });

/** ROADMAP #239 — did the server refuse this upload for its SESSION (an HTTP 401, or an OpResult auth code in the body)? */
export const isSessionRefusalAnswer = (answer: UploadAnswer): boolean => {
  if (answer.status === 401) {
    return true;
  }

  try {
    const code = (JSON.parse(answer.text) as { code?: unknown })?.code;

    return typeof code === "number" && isAuthErrorCode(code);
  } catch {
    return false;
  }
};

/**
 * ROADMAP #239 — `postFormWithStallLimit`, renewing an EXPIRED session once and sending the same form again (an upload
 * goes over REST, so the GraphQL refresh in ApiService never sees it). ⓘ Safe to repeat: an auth refusal is answered
 * before anything is stored. With no refresher (a crew session, a site without one) it is exactly one upload.
 */
export const postFormWithSessionRetry = async (options: StallLimitedUploadOptions): Promise<UploadAnswer> => {
  const canRenew = hasSessionRefresher();
  let renewFailed = false;

  // ⚠ Renew FIRST when the token is known to be (nearly) expired: an upload to an event answers an expired token as
  //   "event not found" (it reads the token only if there is one), never as a refusal to renew on.
  if (canRenew && sessionNeedsRenewal()) {
    renewFailed = !(await refreshSessionOnce(Date.now()));
  }

  const sentAt = Date.now();
  const answer = await postFormWithStallLimit(options);

  if (isSessionRefusalAnswer(answer) && canRenew && !renewFailed && (await refreshSessionOnce(sentAt))) {
    return postFormWithStallLimit(options);
  }

  return answer;
};
